import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Clear as ClearIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const MATCH_LEVELS = [
  { value: 'PERFECT_MATCH', label: 'Perfect Match' },
  { value: 'COLOR_PRODUCT_MATCH', label: 'Color Product Match' },
  { value: 'COLOR_RETAILER_MATCH', label: 'Color Retailer Match' },
  { value: 'RETAILER_ONLY_MATCH', label: 'Retailer Only Match' },
  { value: 'NO_MATCH', label: 'No Match' },
];

const STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved (All)' },
  { value: 'auto_approved', label: '\u00A0\u00A0↳ Auto Approved' },
  { value: 'manually_approved', label: '\u00A0\u00A0↳ Manually Approved' },
  { value: 'declined', label: 'Declined' },
];

export default function CommissionFilters({
  filters,
  retailers,
  onFilterChange,
  onClearFilters,
  onRefresh,
}) {
  const handleChange = (field) => (event) => {
    onFilterChange({ ...filters, [field]: event.target.value });
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== '' && v !== null && v !== undefined
  ).length;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        {/* Selling Retailer */}
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 180 } }}>
          <InputLabel shrink>Selling Retailer</InputLabel>
          <Select
            value={filters.selling_retailer_id || ''}
            onChange={handleChange('selling_retailer_id')}
            label="Selling Retailer"
            displayEmpty
            notched
          >
            <MenuItem value="">All</MenuItem>
            {retailers.map((r) => (
              <MenuItem key={r.retailer_id} value={r.retailer_id}>
                {r.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Buying Retailer */}
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 180 } }}>
          <InputLabel shrink>Buying Retailer</InputLabel>
          <Select
            value={filters.buying_retailer_id || ''}
            onChange={handleChange('buying_retailer_id')}
            label="Buying Retailer"
            displayEmpty
            notched
          >
            <MenuItem value="">All</MenuItem>
            {retailers.map((r) => (
              <MenuItem key={r.retailer_id} value={r.retailer_id}>
                {r.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status */}
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 150 } }}>
          <InputLabel shrink>Status</InputLabel>
          <Select
            value={filters.status || ''}
            onChange={handleChange('status')}
            label="Status"
            displayEmpty
            notched
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Match Level */}
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 180 } }}>
          <InputLabel shrink>Match Level</InputLabel>
          <Select
            value={filters.match_level || ''}
            onChange={handleChange('match_level')}
            label="Match Level"
            displayEmpty
            notched
          >
            <MenuItem value="">All</MenuItem>
            {MATCH_LEVELS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <TextField
          size="small"
          type="date"
          label="From Date"
          value={filters.from_date || ''}
          onChange={handleChange('from_date')}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '47%', sm: 150 } }}
        />
        <TextField
          size="small"
          type="date"
          label="To Date"
          value={filters.to_date || ''}
          onChange={handleChange('to_date')}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '47%', sm: 150 } }}
        />

        {/* Invoice Number Search */}
        <TextField
          size="small"
          label="Invoice #"
          value={filters.invoice_number || ''}
          onChange={handleChange('invoice_number')}
          placeholder="Search invoice..."
          sx={{ width: { xs: '47%', sm: 140 } }}
        />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeFilterCount > 0 && (
            <Tooltip title="Clear all filters">
              <Chip
                label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
                onDelete={onClearFilters}
                deleteIcon={<ClearIcon />}
                size="small"
              />
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
    </Box>
  );
}
